import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { questionClassesQueryOptions } from '@/features/question/api/question.api';
import { useSaveMarketProject } from '../hooks/useMpP2Mutations';
import type { MarketProject } from '../types/marketPromotionP2.types';

interface MarketProjectFormDialogProps {
  project: MarketProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  name: string;
  code: string;
  classId: number | undefined;
  startDate: string;
  deadlineDate: string;
  comments: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  name: '',
  code: '',
  classId: undefined,
  startDate: '',
  deadlineDate: '',
  comments: '',
  isActive: true,
};

const ACTIVE_OPTIONS = [
  { id: 1, name: 'Active' },
  { id: 0, name: 'Inactive' },
];

export function MarketProjectFormDialog({
  project,
  open,
  onOpenChange,
}: MarketProjectFormDialogProps) {
  const [form, setForm] = useState<FormState>(() =>
    project === null
      ? EMPTY
      : {
          name: project.name ?? '',
          code: project.code ?? '',
          classId: project.classId ?? undefined,
          startDate: project.startDate?.slice(0, 10) ?? '',
          deadlineDate: project.deadlineDate?.slice(0, 10) ?? '',
          comments: project.comments ?? '',
          isActive: project.isActive,
        },
  );
  const { data: classes } = useQuery(questionClassesQueryOptions());

  const save = useSaveMarketProject(() => onOpenChange(false));

  const patch = (next: Partial<FormState>) => setForm((prev) => ({ ...prev, ...next }));

  function submit() {
    save.mutate({
      id: project?.id ?? null,
      payload: {
        name: form.name.trim(),
        ...(form.code.trim() === '' ? {} : { code: form.code.trim() }),
        ...(form.classId === undefined ? {} : { classId: form.classId }),
        ...(form.startDate === '' ? {} : { startDate: form.startDate }),
        ...(form.deadlineDate === '' ? {} : { deadlineDate: form.deadlineDate }),
        ...(form.comments.trim() === '' ? {} : { comments: form.comments.trim() }),
        isActive: form.isActive,
      },
    });
  }

  const classOptions = (classes ?? []).map((row) => ({ id: row.id, name: row.name }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <div className="min-w-0">
          <DialogHeader>
            <DialogTitle>{project === null ? 'Add Project' : 'Edit Project'}</DialogTitle>
            <DialogDescription>
              A market promotion project a budget can be declared against.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpp-name">Name</Label>
              <Input
                id="mpp-name"
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
                placeholder="General Cash Collection FY2027-2028"
                autoComplete="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpp-code">Code</Label>
              <Input
                id="mpp-code"
                value={form.code}
                onChange={(event) => patch({ code: event.target.value })}
                placeholder="GCC27-28"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpp-class">Class</Label>
              <OptionSelect
                id="mpp-class"
                options={classOptions}
                value={form.classId}
                onChange={(classId) => patch({ classId })}
                placeholder="No class"
                clearable
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpp-start">Start Date</Label>
              <DatePickerInput
                id="mpp-start"
                value={form.startDate}
                onChange={(startDate) => patch({ startDate })}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpp-deadline">Deadline</Label>
              <DatePickerInput
                id="mpp-deadline"
                value={form.deadlineDate}
                onChange={(deadlineDate) => patch({ deadlineDate })}
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpp-comments">Comments</Label>
              <Input
                id="mpp-comments"
                value={form.comments}
                onChange={(event) => patch({ comments: event.target.value })}
                placeholder="Optional"
                autoComplete="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpp-status">Status</Label>
              <OptionSelect
                id="mpp-status"
                options={ACTIVE_OPTIONS}
                value={form.isActive ? 1 : 0}
                onChange={(value) => patch({ isActive: value !== 0 })}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={save.isPending || form.name.trim() === ''}
            >
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {project === null ? 'Create' : 'Update'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
